import React, { useEffect, useState } from "react";
import * as Sentry from "@sentry/react-native";
import { BlurView } from "expo-blur";
import { StyleSheet, View } from "react-native";

import UploadPublicPhotoPrompt from "./UploadPublicPhotoPrompt";

import ImageGrid from "@/Components/Media/User/Layout/UserImageGrid";
import { type AppUserSession } from "@/schemas/auth/session";
import { type UserProfile } from "@/schemas/user/profile";
import MediaService from "@/services/user/media";
import {
  UserImageType,
  type UserLikableImageMedia,
} from "@/schemas/user/image";
import UserImageActionableCard from "@/Components/Media/User/Images/UserImageActionableCard";

type PublicPhotoTabProps = {
  session: AppUserSession;
  profile: UserProfile;
};

function PublicPhotoTab({
  session,
  profile,
}: PublicPhotoTabProps): React.JSX.Element {
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [images, setImages] = useState<UserLikableImageMedia[]>([]);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [viewerHasNoPublicImage, setViewerHasNoPublicImage] =
    useState<boolean>(false);

  const renderImageOrActionCard = ({
    item,
  }: {
    item: UserLikableImageMedia | null;
  }): React.JSX.Element => {
    if (item === null) {
      return <></>;
    }
    return (
      <UserImageActionableCard
        userLikableImage={item}
        session={session}
        isReadOnly={true}
      />
    );
  };

  const handleUploadSuccess = (): void => {
    setViewerHasNoPublicImage(false);
  };

  useEffect(() => {
    const ms = new MediaService(session);

    const fetchImages = async (): Promise<void> => {
      setIsLoading(true);
      try {
        const response = await ms.getImages(
          profile.username,
          UserImageType.PUBLIC_IMAGE,
          { limit: 10, next: null },
        );

        setImages(response.items);
        setNextCursor(response.next);
      } finally {
        setIsLoading(false);
      }
    };

    const checkAndSetViewerHasPublicImage = async (): Promise<void> => {
      const viewerUsername = session.identity.username;
      const response = await ms.getImages(
        viewerUsername,
        UserImageType.PUBLIC_IMAGE,
        { limit: 1, next: null },
      );

      setViewerHasNoPublicImage(response.total === 0);
    };

    if (session.identity.username !== profile.username) {
      checkAndSetViewerHasPublicImage();
    }

    fetchImages();
  }, [profile.username, session]);

  const loadMoreImages = async (): Promise<void> => {
    if (!nextCursor) return;

    setIsLoadingMore(true);
    try {
      const ms = new MediaService(session);
      const response = await ms.getImages(
        profile.username,
        UserImageType.PUBLIC_IMAGE,
        { limit: 10, next: nextCursor },
      );

      setImages([...images, ...response.items]);
      setNextCursor(response.next);
    } catch (error) {
      Sentry.captureException(error);
    } finally {
      setIsLoadingMore(false);
    }
  };

  return (
    <>
      <ImageGrid
        images={images}
        isLoading={isLoading}
        isLoadingMore={isLoadingMore}
        onRefresh={() => {}}
        onLoadMore={loadMoreImages}
        renderImage={renderImageOrActionCard}
      />
      {viewerHasNoPublicImage && (
        <>
          <BlurView
            experimentalBlurMethod={"dimezisBlurView"}
            intensity={50}
            tint={"extraLight"}
            style={styles.absoluteFill}
          />
          <View style={styles.absoluteFill}>
            <UploadPublicPhotoPrompt
              session={session}
              onUploadSuccess={handleUploadSuccess}
            />
          </View>
        </>
      )}
    </>
  );
}

const styles = StyleSheet.create({
  absoluteFill: {
    ...StyleSheet.absoluteFillObject,
  },
});

export default PublicPhotoTab;
